import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import TopBar from '../components/TopBar';
import BottomNav from '../components/BottomNav';
import {
  ReviewRequest,
  subscribeToRabbiReviewRequests,
  respondToReviewRequest,
  deleteReviewRequest,
  ReviewStatus,
} from '../utils/reviewRequests';
import {
  AuditStats,
  subscribeToRabbiAuditStats,
  getAllAuditEntriesForRabbi,
  AuditLogEntry,
} from '../utils/auditLog';
import StudentsList from '../components/StudentsList';
import { formatNumberForLocale } from '../utils/hebrewNumbers';
import { FIREBASE_FUNCTIONS, callCloudFunction } from '../utils/firebaseFunctions';

export interface RabbiInfoPortal {
  id: string;
  key?: string;
  nusach?: string;
  nameHe?: string;
  nameEn?: string;
  firstName?: string;
  lastName?: string;
}

const RabbiPortalPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isHebrew = i18n.language?.startsWith('he');

  // Review requests state
  const [reviewRequests, setReviewRequests] = useState<ReviewRequest[]>([]);
  const [loadingRequests, setLoadingRequests] = useState(true);
  const [playingRequestId, setPlayingRequestId] = useState<string | null>(null);
  const [selectedRequest, setSelectedRequest] = useState<ReviewRequest | null>(null);
  const [responseText, setResponseText] = useState('');
  const [submittingResponse, setSubmittingResponse] = useState(false);
  const [deletingAll, setDeletingAll] = useState(false);
  const [showDeleteAllConfirm, setShowDeleteAllConfirm] = useState(false);
  const audioRef = useRef<HTMLAudioElement>(null);

  // Audit stats state
  const [auditStats, setAuditStats] = useState<AuditStats | null>(null);
  const [loadingStats, setLoadingStats] = useState(true);
  const [allAuditEntries, setAllAuditEntries] = useState<AuditLogEntry[]>([]);
  const [loadingAuditEntries, setLoadingAuditEntries] = useState(true);

  const [linkedRabbis, setLinkedRabbis] = useState<RabbiInfoPortal[]>([]);
  const [effectiveRabbi, setEffectiveRabbi] = useState<RabbiInfoPortal | null>(null);

  const rabbiInfoFromStorage = useMemo(() => {
    const stored = localStorage.getItem('rabbiInfo');
    const token = localStorage.getItem('rabbiToken');
    if (!stored && !token) return null;
    try {
      const parsed = stored ? (JSON.parse(stored) as Record<string, unknown>) : {};
      const id = (parsed.id as string) || token || '';
      const rabbi: RabbiInfoPortal = {
        id,
        key: (parsed.key as string) || id,
        nusach: parsed.nusach as string | undefined,
        nameHe: parsed.nameHe as string | undefined,
        nameEn: parsed.nameEn as string | undefined,
        firstName: parsed.firstName as string | undefined,
        lastName: parsed.lastName as string | undefined,
      };
      return rabbi;
    } catch {
      const token = localStorage.getItem('rabbiToken');
      return token ? { id: token, key: token } : null;
    }
  }, []);

  useEffect(() => {
    if (rabbiInfoFromStorage && !effectiveRabbi) {
      setEffectiveRabbi(rabbiInfoFromStorage);
    }
  }, [rabbiInfoFromStorage, effectiveRabbi]);

  useEffect(() => {
    const token = localStorage.getItem('rabbiToken');
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await callCloudFunction(
          'getLinkedRabbis',
          FIREBASE_FUNCTIONS.getLinkedRabbis(),
          {
            method: 'GET',
            headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
          },
        );
        if (cancelled || !resp.ok) return;
        const data = await resp.json();
        if (data.rabbis && Array.isArray(data.rabbis)) {
          const list = data.rabbis.map((r: RabbiInfoPortal) => ({
            id: r.id,
            key: r.key,
            nusach: r.nusach,
            nameHe: r.nameHe,
            nameEn: r.nameEn,
            firstName: r.firstName,
            lastName: r.lastName,
          }));
          setLinkedRabbis(list);
        }
      } catch (e) {
        if (!cancelled) console.error('Failed to fetch linked rabbis:', e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [rabbiInfoFromStorage?.id]);

  // Subscribe to review requests (use effective rabbi id and nusach)
  useEffect(() => {
    if (!effectiveRabbi?.id) {
      setLoadingRequests(false);
      return;
    }

    setLoadingRequests(true);
    const unsubscribe = subscribeToRabbiReviewRequests(effectiveRabbi.id, (requests) => {
      setReviewRequests(requests);
      setLoadingRequests(false);
    });

    return () => unsubscribe();
  }, [effectiveRabbi?.id]);

  // Subscribe to audit stats (use effective rabbi id and nusach)
  useEffect(() => {
    if (!effectiveRabbi?.id) {
      setLoadingStats(false);
      setLoadingAuditEntries(false);
      return;
    }

    setLoadingStats(true);
    const unsubscribe = subscribeToRabbiAuditStats(effectiveRabbi.id, (stats) => {
      setAuditStats(stats);
      setLoadingStats(false);
    });

    return () => unsubscribe();
  }, [effectiveRabbi?.id]);

  // Load all audit entries for students list (use effective rabbi id and nusach)
  useEffect(() => {
    if (!effectiveRabbi?.id) {
      setLoadingAuditEntries(false);
      return;
    }

    setLoadingAuditEntries(true);
    getAllAuditEntriesForRabbi(effectiveRabbi.id)
      .then((entries) => {
        setAllAuditEntries(
          entries.filter((entry) => entry.userEmail !== 'david.cohen@example.com'),
        );
        setLoadingAuditEntries(false);
      })
      .catch((error) => {
        console.error('Error loading audit entries:', error);
        setLoadingAuditEntries(false);
      });
  }, [effectiveRabbi?.id]);

  // Unique students count from the same filtered list as StudentsList (so card and list match)
  const displayUniqueListeners = useMemo(() => {
    if (loadingAuditEntries) return auditStats?.uniqueListeners ?? 0;
    const uniqueIds = new Set(allAuditEntries.map((e) => e.userId));
    return uniqueIds.size;
  }, [allAuditEntries, loadingAuditEntries, auditStats?.uniqueListeners]);

  const pendingRequests = useMemo(
    () => reviewRequests.filter((r) => r.status === 'pending'),
    [reviewRequests],
  );

  const reviewedRequests = useMemo(
    () => reviewRequests.filter((r) => r.status !== 'pending'),
    [reviewRequests],
  );

  const handlePlayAudio = (request: ReviewRequest) => {
    if (!audioRef.current) return;

    if (playingRequestId === request.id) {
      audioRef.current.pause();
      setPlayingRequestId(null);
    } else {
      audioRef.current.src = request.audioUrl;
      audioRef.current.play();
      setPlayingRequestId(request.id || null);
    }
  };

  const handleOpenResponse = (request: ReviewRequest) => {
    setSelectedRequest(request);
    setResponseText('');
  };

  const handleSubmitResponse = async (status: ReviewStatus) => {
    if (!selectedRequest?.id) return;

    setSubmittingResponse(true);
    try {
      await respondToReviewRequest(selectedRequest.id, {
        status,
        rabbiResponse: responseText || undefined,
      });
      setSelectedRequest(null);
      setResponseText('');
    } catch (error) {
      console.error('Error submitting response:', error);
    } finally {
      setSubmittingResponse(false);
    }
  };

  const handleDeleteAllReviewed = async () => {
    if (reviewedRequests.length === 0) return;

    setDeletingAll(true);
    try {
      // Delete all reviewed requests
      await Promise.all(
        reviewedRequests.map((request) =>
          request.id ? deleteReviewRequest(request.id) : Promise.resolve(),
        ),
      );
      setShowDeleteAllConfirm(false);
    } catch (error) {
      console.error('Error deleting review requests:', error);
    } finally {
      setDeletingAll(false);
    }
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString(isHebrew ? 'he-IL' : 'en-US', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div
      className="h-full flex flex-col"
      style={{ background: 'var(--kol-white-soft, #F8F9FA)', color: 'var(--kol-navy, #132A49)' }}
    >
      <TopBar title={t('rabbi_portal_title') || 'Rabbi Portal'} />
      <div className="flex-1 px-5 pt-6 pb-24 space-y-6 overflow-y-auto">
        {linkedRabbis.length > 1 && (
          <div dir={isHebrew ? 'rtl' : 'ltr'} className="mb-2">
            <label className="block text-sm font-medium mb-2" htmlFor="rabbi-portal-nusach-select">
              {t('rabbi_upload_select_nusach') || 'Select nusach to view and upload'}
            </label>
            <select
              id="rabbi-portal-nusach-select"
              value={effectiveRabbi?.id ?? ''}
              onChange={(e) => {
                const id = e.target.value;
                const selected = linkedRabbis.find((r) => r.id === id);
                if (selected) setEffectiveRabbi(selected);
              }}
              className="w-full py-3 px-4 rounded-2xl border-2 border-[#caa42e] text-sm bg-white text-[#132a49] cursor-pointer appearance-none focus:outline-none focus:ring-2 focus:ring-[#caa42e]"
              style={{
                textAlign: isHebrew ? 'right' : 'left',
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23132a49' d='M6 8L1 3h10z'/%3E%3C/svg%3E")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 14px center',
                paddingRight: '36px',
              }}
            >
              {linkedRabbis.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.nusach || r.id}
                </option>
              ))}
            </select>
          </div>
        )}
        {/* Quick management */}
        <div dir={isHebrew ? 'rtl' : 'ltr'}>
          <h2 className={`text-lg font-semibold mb-3 ${isHebrew ? 'text-right' : 'text-left'}`}>
            {t('rabbi_portal_quick_management') || 'Quick Management'}
          </h2>
          <div className="flex gap-3 w-full">
            <button
              type="button"
              onClick={() => navigate('/rabbi-upload')}
              className="w-full bg-white text-[#132a49] rounded-2xl p-4 shadow-md border border-[#e5e7eb] hover:scale-[1.025] active:scale-100 transition-all flex flex-col items-center group"
              style={{ flex: 1, direction: isHebrew ? 'rtl' : 'ltr' }}
            >
              <div className="w-12 h-12 rounded-full flex items-center justify-center mb-2 shadow group-hover:bg-[#e6eefd] transition">
                {/* Upload Cloud Icon (Heroicons Outline) */}
                {/* Microphone icon for audio upload */}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.7}
                  stroke="#3555b7"
                  className="w-7 h-7"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 18v2m0 0h3m-3 0H9m6-10a3 3 0 1 0-6 0v4a3 3 0 1 0 6 0v-4ZM19 12v1a7 7 0 1 1-14 0v-1"
                  />
                </svg>
              </div>
              <div className={`text-base font-semibold ${isHebrew ? 'text-right' : 'text-left'}`}>
                {t('rabbi_portal_upload_card') || 'Upload'}
              </div>
              <span className="text-xs mt-0.5 text-gray-500 group-hover:text-[#3555b7] transition">
                {t('upload_learning_record') || 'Upload new learning record'}
              </span>
            </button>
          </div>
        </div>

        {/* Listening Statistics */}
        <div dir={isHebrew ? 'rtl' : 'ltr'}>
          <div className="flex items-center gap-2 mb-3">
            <span className="text-2xl">📈</span>
            <h2 className={`text-lg font-semibold ${isHebrew ? 'text-right' : 'text-left'}`}>
              {t('your_impact') || 'Your Impact'}
            </h2>
          </div>
          {loadingStats ? (
            <div
              className="rounded-2xl shadow-md p-6 flex items-center justify-center"
              style={{
                background: 'var(--kol-gold-cream, #F5EED6)',
                border: '1px solid var(--kol-gold-light, #D4B96A)',
              }}
            >
              <div
                className="animate-spin w-6 h-6 border-2 border-t-transparent rounded-full"
                style={{ borderColor: 'var(--kol-gold-primary, #C9A857)' }}
              ></div>
              <span className="ml-3" style={{ color: 'var(--kol-gold-dark, #8B7532)' }}>
                {t('loading') || 'Loading...'}
              </span>
            </div>
          ) : auditStats && auditStats.totalPlays > 0 ? (
            <div
              className="rounded-2xl shadow-lg p-4 relative overflow-hidden"
              style={{
                background: 'var(--kol-gold-cream, #F5EED6)',
                border: '1px solid var(--kol-gold-light, #D4B96A)',
                boxShadow: 'var(--kol-shadow-md, 0 4px 12px rgba(10, 22, 40, 0.12))',
              }}
            >
              {/* Decorative background elements */}
              <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-[#fbbf24]/20 to-transparent rounded-full -translate-y-1/2 translate-x-1/2"></div>
              <div className="absolute bottom-0 left-0 w-20 h-20 bg-gradient-to-tr from-[#22c55e]/10 to-transparent rounded-full translate-y-1/2 -translate-x-1/2"></div>

              {/* Encouraging Header */}
              <div className="text-center mb-3 relative z-10">
                <div className="inline-flex items-center gap-2 bg-white/70 backdrop-blur-sm px-3 py-1.5 rounded-full shadow-sm">
                  <span className="text-base">🌟</span>
                  <span className="text-xs font-semibold text-[#854d0e]">
                    {t('students_learning_from_you') || 'Students are learning from you!'}
                  </span>
                </div>
              </div>

              {/* Stats Overview Grid */}
              <div className="grid grid-cols-2 gap-3 mb-4 relative z-10">
                {/* Total Plays */}
                <div className="bg-white/80 backdrop-blur-sm rounded-xl p-3 text-center shadow-md border border-white/50 hover:scale-105 transition-transform">
                  <div
                    className="w-10 h-10 mx-auto mb-1.5 rounded-full flex items-center justify-center shadow-lg"
                    style={{ background: 'var(--kol-navy, #132A49)' }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={2}
                      stroke="white"
                      className="w-5 h-5"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M5.25 5.653c0-.856.917-1.398 1.667-.986l11.54 6.347a1.125 1.125 0 0 1 0 1.972l-11.54 6.347a1.125 1.125 0 0 1-1.667-.986V5.653Z"
                      />
                    </svg>
                  </div>
                  <div className="text-3xl font-bold" style={{ color: 'var(--kol-navy, #132A49)' }}>
                    {auditStats.totalPlays}
                  </div>
                  <div
                    className="text-[10px] font-semibold uppercase tracking-wide"
                    style={{ color: 'var(--kol-navy, #132A49)' }}
                  >
                    {t('total_plays') || 'Total Plays'}
                  </div>
                </div>
                {/* Unique Listeners */}
                <div className="bg-white/80 backdrop-blur-sm rounded-xl p-3 text-center shadow-md border border-white/50 hover:scale-105 transition-transform">
                  <div
                    className="w-10 h-10 mx-auto mb-1.5 rounded-full flex items-center justify-center shadow-lg"
                    style={{
                      background:
                        'var(--kol-gradient-gold, linear-gradient(90deg, #C9A857 0%, #B8924F 100%))',
                    }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={2}
                      stroke="white"
                      className="w-5 h-5"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z"
                      />
                    </svg>
                  </div>
                  <div
                    className="text-3xl font-bold"
                    style={{ color: 'var(--kol-gold-primary, #C9A857)' }}
                  >
                    {displayUniqueListeners}
                  </div>
                  <div
                    className="text-[10px] font-semibold uppercase tracking-wide"
                    style={{ color: 'var(--kol-gold-primary, #C9A857)' }}
                  >
                    {t('unique_listeners') || 'Students'}
                  </div>
                </div>
              </div>

              {/* Plays by Parasha */}
              {Object.keys(auditStats.playsByParasha).length > 0 && (
                <div className="mb-3 relative z-10">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-base">📖</span>
                    <h4 className="text-xs font-bold text-[#854d0e]">
                      {t('popular_parashiot') || 'Most Listened Parashiot'}
                    </h4>
                  </div>
                  <div className="space-y-1.5">
                    {Object.entries(auditStats.playsByParasha)
                      .sort((a, b) => b[1] - a[1])
                      .slice(0, 3)
                      .map(([parasha, count], index) => {
                        const maxCount = Math.max(...Object.values(auditStats.playsByParasha));
                        const percentage = (count / maxCount) * 100;
                        return (
                          <div
                            key={parasha}
                            className="bg-white/70 backdrop-blur-sm rounded-lg px-3 py-2 shadow-sm border border-white/50"
                          >
                            <div className="flex items-center justify-between mb-1">
                              <div className="flex items-center gap-1.5">
                                {index === 0 && <span className="text-xs">🥇</span>}
                                {index === 1 && <span className="text-xs">🥈</span>}
                                {index === 2 && <span className="text-xs">🥉</span>}
                                <span className="text-xs text-[#132a49] font-semibold">
                                  {parasha}
                                </span>
                              </div>
                              <span className="text-xs text-[#6366f1] font-bold">{count}</span>
                            </div>
                            <div className="h-1.5 bg-[#e5e7eb]/50 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-gradient-to-r from-[#6366f1] to-[#a78bfa] rounded-full transition-all duration-500"
                                style={{ width: `${percentage}%` }}
                              ></div>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}

              {/* All Students List */}
              {!loadingAuditEntries && allAuditEntries.length > 0 && (
                <div className="relative z-10">
                  <StudentsList auditEntries={allAuditEntries} isHebrew={isHebrew} />
                </div>
              )}

              {/* Motivational Footer */}
              <div className="mt-3 text-center relative z-10">
                <div className="inline-flex items-center gap-1.5 text-xs text-[#854d0e] bg-white/50 px-3 py-1.5 rounded-full">
                  <span>✨</span>
                  <span className="font-medium">
                    {t('keep_inspiring') || 'Keep inspiring your students!'}
                  </span>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-gradient-to-br from-[#f0f9ff] via-[#e0f2fe] to-[#bae6fd] rounded-2xl shadow-lg border border-[#7dd3fc] p-6 text-center relative overflow-hidden">
              {/* Decorative elements */}
              <div className="absolute top-3 left-3 text-3xl opacity-20">🎵</div>
              <div className="absolute bottom-3 right-3 text-3xl opacity-20">📚</div>

              <div className="relative z-10">
                <div className="w-16 h-16 mx-auto mb-3 bg-gradient-to-br from-[#0ea5e9] to-[#0284c7] rounded-full flex items-center justify-center shadow-xl">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="white"
                    className="w-8 h-8"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 0 1 3 19.875v-6.75ZM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V8.625ZM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V4.125Z"
                    />
                  </svg>
                </div>
                <h3 className="text-lg font-bold text-[#0369a1] mb-1">
                  {t('ready_to_inspire') || 'Ready to Inspire?'}
                </h3>
                <p className="text-sm text-[#0c4a6e] mb-3 max-w-xs mx-auto">
                  {t('no_listening_data_encouraging') ||
                    'Upload your first recording and watch your students grow!'}
                </p>
                <button
                  onClick={() => navigate('/rabbi-upload')}
                  className="inline-flex items-center gap-2 bg-gradient-to-r from-[#0ea5e9] to-[#0284c7] text-white font-semibold px-5 py-2.5 rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={2}
                    stroke="currentColor"
                    className="w-4 h-4"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                  </svg>
                  {t('upload_first_recording') || 'Upload Your First Recording'}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Pending Review Requests */}
        {loadingRequests ? (
          <div className="bg-white rounded-2xl shadow-md border border-[#e5e7eb] p-6 flex items-center justify-center">
            <div className="animate-spin w-6 h-6 border-2 border-[#6366f1] border-t-transparent rounded-full"></div>
            <span className="ml-3 text-gray-500">{t('loading') || 'Loading...'}</span>
          </div>
        ) : pendingRequests.length > 0 ? (
          <div
            className="rounded-2xl shadow-md p-4"
            style={{
              direction: isHebrew ? 'rtl' : 'ltr',
              background: 'var(--kol-gold-cream, #F5EED6)',
              border: '1px solid var(--kol-gold, #B8924F)',
              boxShadow: 'var(--kol-shadow-md, 0 4px 12px rgba(10, 22, 40, 0.12))',
            }}
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold text-[#92400e] flex items-center gap-2">
                🔔 {t('pending_reviews') || 'Pending Reviews'}
                <span className="bg-[#f59e0b] text-white text-xs px-2 py-0.5 rounded-full">
                  {pendingRequests.length}
                </span>
              </h3>
            </div>
            <div className="flex flex-col gap-3">
              {pendingRequests.map((request) => (
                <div
                  key={request.id}
                  className="bg-white rounded-xl p-4 shadow-sm border border-[#fcd34d]"
                >
                  <div className="flex items-center gap-3 mb-3">
                    {request.studentPhotoUrl ? (
                      <img
                        src={request.studentPhotoUrl}
                        alt={request.studentName}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                    ) : (
                      <div
                        className="w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold"
                        style={{ background: 'var(--kol-navy, #132A49)' }}
                      >
                        {request.studentName?.[0]?.toUpperCase() || '?'}
                      </div>
                    )}
                    <div className="flex-1">
                      <div className="font-semibold text-[#132a49]">{request.studentName}</div>
                      <div className="text-xs text-gray-500">
                        {request.parashaHebrew || request.parashaKey} · {t('verse') || 'Verse'}{' '}
                        {formatNumberForLocale(request.verseIndex + 1, i18n.language || '')}
                      </div>
                    </div>
                    <div className="text-xs text-gray-400">{formatDate(request.createdAt)}</div>
                  </div>

                  {/* Verse text preview */}
                  <div
                    className="bg-[#f8f9fa] rounded-lg p-3 mb-3 text-sm text-[#132a49] leading-relaxed line-clamp-2"
                    dir="rtl"
                  >
                    {request.verseText}
                  </div>

                  {/* Action buttons */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => handlePlayAudio(request)}
                      className={`flex-1 py-2 rounded-lg font-medium text-sm flex items-center justify-center gap-1 transition-colors ${
                        playingRequestId === request.id
                          ? 'bg-[#132a49] text-white'
                          : 'bg-[#f0f9ff] text-[#132a49] border border-[#132a49]'
                      }`}
                    >
                      {playingRequestId === request.id ? '❚❚' : '▶'} {t('listen') || 'Listen'}
                    </button>
                    <button
                      onClick={() => handleOpenResponse(request)}
                      className="flex-1 py-2 rounded-lg bg-[#6366f1] text-white font-medium text-sm hover:bg-[#4f46e5] transition-colors"
                    >
                      📝 {t('respond') || 'Respond'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div
            style={{ direction: isHebrew ? 'rtl' : 'ltr' }}
            className="bg-white rounded-2xl shadow-md border border-[#e5e7eb] p-6 text-center"
          >
            <div className="text-4xl mb-2">✨</div>
            <h3 className="text-lg font-semibold text-[#132a49] mb-1">
              {t('no_pending_reviews') || 'No Pending Reviews'}
            </h3>
            <p className="text-sm text-gray-500">
              {t('no_pending_reviews_desc') ||
                "You're all caught up! No student recordings waiting for review."}
            </p>
          </div>
        )}

        {/* Previously Reviewed */}
        {reviewedRequests.length > 0 && (
          <div
            style={{ direction: isHebrew ? 'rtl' : 'ltr' }}
            className="bg-white rounded-2xl shadow-md border border-[#e5e7eb] p-4"
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold">✓ {t('reviewed') || 'Reviewed'}</h3>
              {/* Delete All button */}
              <button
                onClick={() => setShowDeleteAllConfirm(true)}
                className="px-3 py-1.5 rounded-lg text-xs font-medium text-red-500 border border-red-200 bg-red-50 hover:text-red-600 hover:bg-red-100 hover:border-red-300 transition-colors flex items-center gap-1.5"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="w-4 h-4"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"
                  />
                </svg>
                {t('delete_all') || 'Delete All'}
              </button>
            </div>
            <div className="flex flex-col divide-y divide-[#f1f2f5]">
              {reviewedRequests.slice(0, 5).map((request) => (
                <div key={request.id} className="py-3 first:pt-0 last:pb-0 flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-[#e5e7eb] flex items-center justify-center text-gray-600 font-semibold text-xs">
                    {request.studentName?.[0]?.toUpperCase() || '?'}
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-medium text-[#132a49]">{request.studentName}</div>
                    <div className="text-xs text-gray-500">
                      {request.parashaHebrew || request.parashaKey} · {t('verse') || 'Verse'}{' '}
                      {formatNumberForLocale(request.verseIndex + 1, i18n.language || '')}
                    </div>
                  </div>
                  <div
                    className={`text-xs px-2 py-1 rounded-full font-medium ${
                      request.status === 'approved'
                        ? 'bg-[#dcfce7] text-[#166534]'
                        : request.status === 'rejected'
                          ? 'bg-[#fee2e2] text-[#991b1b]'
                          : 'bg-[#e0e7ff] text-[#3730a3]'
                    }`}
                  >
                    {request.status === 'approved'
                      ? t('approved') || 'Approved'
                      : request.status === 'rejected'
                        ? t('needs_work') || 'Needs Work'
                        : t('reviewed') || 'Reviewed'}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Schedule */}
        <div
          style={{ direction: isHebrew ? 'rtl' : 'ltr' }}
          className="bg-white rounded-2xl shadow-md border border-[#e5e7eb] p-4"
        >
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-semibold">{t('future_schedule') || "Today's Schedule"}</h3>
          </div>
          <button
            type="button"
            className="w-full mt-3 py-3 rounded-xl border border-dashed border-[#d6d9e0] text-sm text-gray-600 hover:bg-[#f7f7fa]"
          >
            {t('rabbi_portal_add_time_slot') || '+ Add time slot'}
          </button>
        </div>
      </div>

      {/* Audio element for playback */}
      <audio
        ref={audioRef}
        onEnded={() => setPlayingRequestId(null)}
        onPause={() => setPlayingRequestId(null)}
      />

      {/* Response Modal */}
      {selectedRequest && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => setSelectedRequest(null)}
        >
          <div
            className="bg-white rounded-2xl p-6 max-w-md w-full shadow-xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            dir={isHebrew ? 'rtl' : 'ltr'}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-[#132a49]">
                {t('respond_to_student') || 'Respond to Student'}
              </h3>
              <button
                onClick={() => setSelectedRequest(null)}
                className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
              >
                ×
              </button>
            </div>

            {/* Student info */}
            <div className="flex items-center gap-3 mb-4">
              <div
                className="w-12 h-12 rounded-full flex items-center justify-center text-white font-semibold"
                style={{ background: 'var(--kol-navy, #132A49)' }}
              >
                {selectedRequest.studentName?.[0]?.toUpperCase() || '?'}
              </div>
              <div>
                <div className="font-semibold text-[#132a49]">{selectedRequest.studentName}</div>
                <div className="text-sm text-gray-500">
                  {selectedRequest.parashaHebrew || selectedRequest.parashaKey} ·{' '}
                  {t('verse') || 'Verse'}{' '}
                  {formatNumberForLocale(selectedRequest.verseIndex + 1, i18n.language || '')}
                </div>
              </div>
            </div>

            {/* Verse */}
            <div className="bg-[#f8f9fa] rounded-xl p-4 mb-4" dir="rtl">
              <div className="text-lg font-medium text-[#132a49] leading-relaxed">
                {selectedRequest.verseText}
              </div>
            </div>

            {/* Play button */}
            <button
              onClick={() => handlePlayAudio(selectedRequest)}
              className={`w-full py-3 rounded-xl font-semibold mb-4 flex items-center justify-center gap-2 transition-colors ${
                playingRequestId === selectedRequest.id
                  ? 'bg-[#132a49] text-white'
                  : 'bg-[#f0f9ff] text-[#132a49] border border-[#132a49]'
              }`}
            >
              {playingRequestId === selectedRequest.id ? (
                <>❚❚ {t('pause') || 'Pause'}</>
              ) : (
                <>▶ {t('listen_to_recording') || 'Listen to Recording'}</>
              )}
            </button>

            {/* Response textarea */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('feedback_optional') || 'Feedback (optional)'}
              </label>
              <textarea
                value={responseText}
                onChange={(e) => setResponseText(e.target.value)}
                placeholder={t('feedback_placeholder') || 'Add your feedback here...'}
                className="w-full p-3 border border-[#e5e7eb] rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-[#6366f1]"
                rows={3}
              />
            </div>

            {/* Action buttons */}
            <div className="flex gap-3">
              <button
                onClick={() => handleSubmitResponse('rejected')}
                disabled={submittingResponse}
                className="flex-1 py-3 rounded-xl bg-[#fee2e2] text-[#991b1b] font-semibold hover:bg-[#fecaca] transition-colors disabled:opacity-50"
              >
                {t('needs_work') || 'Needs Work'}
              </button>
              <button
                onClick={() => handleSubmitResponse('approved')}
                disabled={submittingResponse}
                className="flex-1 py-3 rounded-xl bg-[#22c55e] text-white font-semibold hover:bg-[#16a34a] transition-colors disabled:opacity-50"
              >
                {submittingResponse ? (
                  <span className="animate-pulse">{t('sending') || 'Sending'}...</span>
                ) : (
                  <>✓ {t('approve') || 'Approve'}</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete All Confirmation Modal */}
      {showDeleteAllConfirm && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowDeleteAllConfirm(false)}
        >
          <div
            className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-xl"
            onClick={(e) => e.stopPropagation()}
            dir={isHebrew ? 'rtl' : 'ltr'}
          >
            <div className="flex items-center justify-center mb-4">
              <div className="w-14 h-14 rounded-full bg-red-100 flex items-center justify-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="#dc2626"
                  className="w-7 h-7"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"
                  />
                </svg>
              </div>
            </div>
            <h3 className="text-lg font-bold text-center text-[#132a49] mb-2">
              {t('delete_all_reviews') || 'Delete All Reviews?'}
            </h3>
            <p className="text-center text-gray-500 text-sm mb-4">
              {t('delete_all_reviews_confirm') ||
                'Are you sure you want to delete all reviewed messages? This action cannot be undone.'}
            </p>
            <div className="bg-[#f8f9fa] rounded-lg p-3 mb-4 text-center">
              <div className="text-2xl font-bold text-red-500">{reviewedRequests.length}</div>
              <div className="text-xs text-gray-500">
                {t('reviews_to_delete') || 'reviews will be deleted'}
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteAllConfirm(false)}
                className="flex-1 py-3 rounded-xl bg-gray-100 text-[#132a49] font-semibold hover:bg-gray-200 transition-colors"
              >
                {t('cancel') || 'Cancel'}
              </button>
              <button
                onClick={handleDeleteAllReviewed}
                disabled={deletingAll}
                className="flex-1 py-3 rounded-xl bg-red-500 text-white font-semibold hover:bg-red-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {deletingAll ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    {t('deleting') || 'Deleting...'}
                  </>
                ) : (
                  <>{t('delete_all') || 'Delete All'}</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <BottomNav />
    </div>
  );
};

export default RabbiPortalPage;
